import { type OccupancyTypeListItem, type OccupancyTypeListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { OccupancyTypeFormDrawer } from '../components/OccupancyTypeFormDrawer';
import { listOccupancyTypes } from '../occupancy-types.api';
import {
  useBulkDeleteOccupancyTypes,
  useBulkSetOccupancyTypesStatus,
  useDeleteOccupancyType,
  useOccupancyTypesList,
} from '../use-occupancy-types';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const MIN_SEARCH_LENGTH = 2;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const OccupancyTypesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteOccupancyType = useDeleteOccupancyType();
  const bulkDeleteMut = useBulkDeleteOccupancyTypes();
  const bulkSetStatusMut = useBulkSetOccupancyTypesStatus();

  const columns: ListScreenColumns<OccupancyTypeListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<OccupancyTypeListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'paxCount',
        header: 'Pax',
        sortable: true,
        width: '100px',
        cell: (row) => (
          <span className="font-medium text-zinc-900">
            {row.paxCount} {row.paxCount === 1 ? 'pax' : 'pax'}
          </span>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <div className="flex items-center gap-2">
            <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<OccupancyTypeListItem, OccupancyTypeListQuery>
      title="Occupancy Types"
      description="Room occupancy. The pax count drives pricing roll-up when components are added to an itinerary."
      useList={useOccupancyTypesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add occupancy type' }}
      renderDrawer={({ open, mode, row, close }) => (
        <OccupancyTypeFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          occupancyType={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteOccupancyType.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this occupancy type?',
            description: `${row.name} will be soft-deleted and hidden from lists.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} occupancy type${rows.length === 1 ? '' : 's'}?`,
            description: 'These occupancy types will be soft-deleted.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} occupancy type${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in the itinerary editor.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listOccupancyTypes(q),
        filename: 'occupancy-types',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Pax count', value: (r) => r.paxCount },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by code or name',
        emptyTitle: 'No occupancy types match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load occupancy types',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
